/**
 * Schema-built canned prompts for ICS-202 Draft with AI. Skips auto boxes,
 * slotted editors, and omitted sources.
 */

import {
  getFormSectionSchema,
  pratusSection,
  sectionHeading,
  type FormSection,
} from './formSectionSchema';
import { COMMUNITY_LIFELINES } from 'components/IAP/planningView/widgets/ics202BoxLabels';
import type { IcsFormFamily } from 'components/IAP/formFamily';

export const ICS_202_BASE_ID = 'ICS_202';

/**
 * Skill the parent agent dispatches (same prefix as useAICommands
 * skillToCommand).
 */
export const ICS_202_DRAFT_SKILL = 'ics_202_draft';

export const ICS_202_SLOT_SOURCES = new Set(['objectives', 'attached']);

export const ICS_202_OMIT_SOURCES = new Set(['otherAttachmentsText']);

export const ICS_202_EXTRA_FIELDS: ReadonlyArray<readonly [string, string]> = [
  ['priorities', 'Incident Priorities'],
  [
    'criticalInformationRequirements',
    'Critical Information Requirements (CIRs)',
  ],
  ['keyDecisionsAndProcedures', 'Key Decisions & Procedures'],
  ['limitationsAndConstraints', 'Limitations & Constraints'],
];

/**
 * Coast Guard boxes 4–13 except box 10 (the signature). Box 6 is objective
 * rows; box 4 is the fixed lifeline list.
 */
export const USCG_ICS202_DRAFT_SOURCES = [
  'communityLifelines',
  'priorities',
  'objectives',
  'operationalPeriodCommandEmphasis',
  'siteSafetyPlan',
  'siteSafetyPlanLocation',
  'criticalInformationRequirements',
  'limitationsAndConstraints',
  'keyDecisionsAndProcedures',
] as const;

const USCG_DRAFT_LABELS: Record<
  (typeof USCG_ICS202_DRAFT_SOURCES)[number],
  readonly [string, FormSection['kind']]
> = {
  communityLifelines: [
    `4. Community Lifelines (${COMMUNITY_LIFELINES.join(', ')})`,
    'text',
  ],
  priorities: ['5. Incident Priorities', 'text'],
  objectives: ['6. Incident Objectives', 'table'],
  operationalPeriodCommandEmphasis: [
    '7. Operational Period Command Emphasis',
    'text',
  ],
  siteSafetyPlan: ['8. Site Safety Plan Required', 'boolean'],
  siteSafetyPlanLocation: ['9. Site Safety Plan located at', 'text'],
  criticalInformationRequirements: [
    '11. Critical Information Requirements',
    'text',
  ],
  limitationsAndConstraints: ['12. Limitations and Constraints', 'text'],
  keyDecisionsAndProcedures: ['13. Key Decisions and Procedures', 'text'],
};

/**
 * Mirror of ICS202_FAMILY_FIELDS in services/coms/src/routes/DGPT/draftForm/
 * ics202Fields.ts: the boxes a top-level Generate drafts, per form family. GET
 * /dice/dgpt/ics202/fields is the source of truth; this seeds it.
 */
export const ICS202_FAMILY_DRAFT_SOURCES: Record<
  IcsFormFamily,
  readonly string[]
> = {
  uscg: USCG_ICS202_DRAFT_SOURCES,
  fema: [
    'objectives',
    'operationalPeriodCommandEmphasis',
    'situationalAwareness',
    'siteSafetyPlan',
    'siteSafetyPlanLocation',
    'priorities',
    'criticalInformationRequirements',
    'keyDecisionsAndProcedures',
    'limitationsAndConstraints',
    'communityLifelines',
  ],
};

export type Ics202FamilyField = { source: string; label: string };

const FALLBACK_LABELS: Record<string, string> = {
  communityLifelines: 'Community Lifelines',
  objectives: 'Incident Objectives',
  situationalAwareness: 'General Situational Awareness',
};

/** The family's draftable boxes with display labels, in printed order. */
export const listIcs202FamilyFields = (
  family: IcsFormFamily
): Ics202FamilyField[] => {
  const known = new Map<string, string>();
  for (const field of [
    ...listIcs202DraftableFields(),
    ...listUscgIcs202DraftFields(),
  ]) {
    if (!known.has(field.source)) known.set(field.source, field.label);
  }
  return ICS202_FAMILY_DRAFT_SOURCES[family].map((source) => ({
    source,
    label: known.get(source) ?? FALLBACK_LABELS[source] ?? source,
  }));
};

/** Composer chip copy for a queued draft. */
export const ics202DraftChipLabel = (
  sources: readonly string[],
  item?: string
): string =>
  sources.length === 1 && item
    ? `Generating ICS-202: ${item}`
    : `Generating ICS-202: ${sources.length} boxes`;

export const ics202FieldLabel = (source: string): string =>
  listIcs202FamilyFields('fema').find((field) => field.source === source)
    ?.label ?? source;

export const listUscgIcs202DraftFields = (): Ics202DraftableField[] =>
  USCG_ICS202_DRAFT_SOURCES.map((source) => {
    const [label, kind] = USCG_DRAFT_LABELS[source];
    return {
      source,
      label,
      instruction: label,
      kind,
    };
  });

export type Ics202DraftableField = {
  source: string;
  label: string;
  instruction: string;
  kind: FormSection['kind'];
};

const isDraftableSection = (section: FormSection): boolean =>
  section.kind !== 'auto' &&
  !ICS_202_SLOT_SOURCES.has(section.source) &&
  !ICS_202_OMIT_SOURCES.has(section.source);

export const listIcs202DraftableFields = (): Ics202DraftableField[] => {
  const schema = getFormSectionSchema(ICS_202_BASE_ID);
  const printed = (schema?.sections ?? [])
    .filter(isDraftableSection)
    .map((section) => ({
      source: section.source,
      label: sectionHeading(section),
      instruction: section.printedLabel || section.label,
      kind: section.kind,
    }));

  const extras = ICS_202_EXTRA_FIELDS.map(([source, label]) => {
    const section = pratusSection(source, label);
    return {
      source,
      label,
      instruction: section.printedLabel,
      kind: section.kind,
    };
  });

  return [...printed, ...extras];
};

/** A box title without its printed box number, trailing colon or option list. */
export const plainLabel = (label: string): string =>
  label
    .replace(/^\s*\d+[a-z]?\.\s*/i, '')
    .replace(/\s*\([^)]*,[^)]*\)\s*$/, '')
    .replace(/:\s*$/, '')
    .trim();

export const buildIcs202CannedPrompt = (
  family: IcsFormFamily,
  sources?: string[]
): string => {
  const familyFields = listIcs202FamilyFields(family);
  const sourcesToList = sources?.length
    ? [
        ...familyFields
          .map((field) => field.source)
          .filter((source) => sources.includes(source)),
        ...sources.filter(
          (source) => !familyFields.some((field) => field.source === source)
        ),
      ]
    : familyFields.map((field) => field.source);
  const labelFor = (source: string) =>
    familyFields.find((field) => field.source === source)?.label ??
    ics202FieldLabel(source);
  const lines = sourcesToList.map(
    (source) => `- ${plainLabel(labelFor(source))}`
  );
  return [
    'Draft the ICS 202 Incident Objectives for the next operational period.',
    'Fill in each box listed below from the data sources selected in Pratus AI, and cite them.',
    ...(sourcesToList.includes('objectives')
      ? [
          'For incident objectives, add new objectives for the next operational period, each with a name and a description. Keep the objectives that already exist.',
        ]
      : []),
    'Ground every box in the current incident data and any governing organization documents.',
    "Write the command emphasis as the Incident Commander's intent for the next operational period: specific, not generic.",
    '',
    'Boxes to draft:',
    ...lines,
  ].join('\n');
};

export const buildIcs202SkillPrompt = (
  sources?: string[],
  family: IcsFormFamily = 'uscg'
): string =>
  `Use the ${ICS_202_DRAFT_SKILL} skill: ${buildIcs202CannedPrompt(family, sources)}`;
